/**
 * AssetBrainstormModal — Generate and explore asset style variations.
 * Same flow as logo brainstorm: generate variations -> pick favorites -> refine -> use this.
 * "Use This" generates all sub-variants (landscape, portrait, templates A/B/C) in the chosen style.
 * Refactored for SDK: accepts a `client` prop (BrandApiClient) instead of importing api.js.
 * Contributor: ben@hapi.vc
 */
import { useState, useEffect, useRef } from 'react';

const TYPE_CONFIG = {
  email_templates: { label: 'Email Templates', subtypes: ['welcome', 'newsletter', 'announcement', 'promotional', 'transactional'], genLabel: 'email styles' },
  social_images: { label: 'Social Media', subtypes: ['instagram_square', 'instagram_story', 'twitter_card', 'linkedin', 'facebook_cover'], genLabel: 'social media styles' },
  page_layouts: { label: 'Page Layouts', subtypes: ['hero', 'features', 'pricing', 'about', 'footer'], genLabel: 'page layout styles' },
};

export default function AssetBrainstormModal({ client, assetType, existingAssets, onClose, onAssetsGenerated }) {
  const config = TYPE_CONFIG[assetType] || { label: assetType, subtypes: [], genLabel: 'styles' };
  const [variations, setVariations] = useState(existingAssets || []);
  const [generating, setGenerating] = useState(false);
  const [generatingMore, setGeneratingMore] = useState(null);
  const [starredIds, setStarredIds] = useState(new Set());
  const [showStarred, setShowStarred] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState('');
  const pollRef = useRef(null);

  // Generate initial variations if none exist
  useEffect(() => {
    if (variations.length === 0) handleGenerate();
  }, []);

  async function handleGenerate() {
    setGenerating(true);
    setProgress('Generating style variations...');
    try {
      const result = await client.generateAssets([assetType], {});
      if (result.job_id) {
        pollRef.current = setInterval(async () => {
          try {
            const job = await client.pollJob(result.job_id);
            const status = job.status || job.job?.status;
            const step = job.current_step || job.job?.current_step || '';
            setProgress(step || 'Generating...');
            if (status === 'completed') {
              clearInterval(pollRef.current);
              const assetsResult = await client.getAssets({ type: assetType.replace(/s$/, '') });
              const allOfType = (assetsResult.assets || assetsResult || []).filter(a => a.status !== 'rejected');
              setVariations(allOfType);
              setGenerating(false);
              setProgress('');
            } else if (status === 'failed') {
              clearInterval(pollRef.current);
              setGenerating(false);
              setProgress('Generation failed');
            }
          } catch {}
        }, 3000);
      }
    } catch (err) {
      setGenerating(false);
      setProgress('Error: ' + err.message);
    }
  }

  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  function toggleSelect(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : prev.length >= 3 ? prev : [...prev, id]);
  }

  function toggleStar(id) {
    setStarredIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  async function handleRefine() {
    if (selectedIds.length === 0 || !feedback.trim()) return;
    setGeneratingMore('refining');
    try {
      const refined = [];
      for (const id of selectedIds) {
        const result = await client.refineAsset(id, `Generate a new variation: ${feedback}. Output only clean HTML.`);
        const newAsset = result.asset || result;
        if (newAsset?.id) refined.push(newAsset);
      }
      setVariations(prev => [...refined, ...prev.filter(a => !refined.some(r => r.id === a.id))]);
      setSelectedIds([]);
      setFeedback('');
    } catch {} finally { setGeneratingMore(null); }
  }

  async function handleMoreLike(assetId) {
    setGeneratingMore(assetId);
    try {
      const result = await client.refineAsset(assetId, `Generate a new variation with a different layout approach. Output only clean HTML.`);
      const newAsset = result.asset || result;
      if (newAsset?.id) {
        setVariations(prev => {
          const idx = prev.findIndex(a => a.id === assetId);
          const copy = [...prev];
          copy.splice(idx >= 0 ? idx + 1 : 0, 0, newAsset);
          return copy;
        });
      }
    } catch {} finally { setGeneratingMore(null); }
  }

  async function handleDelete(assetId) {
    try { await client.rejectAsset(assetId); } catch {}
    setVariations(prev => prev.filter(a => a.id !== assetId));
    setSelectedIds(prev => prev.filter(i => i !== assetId));
  }

  async function handleUseThis(assetId) {
    // "Use This" closes the modal and tells parent to keep this asset
    // The parent can then generate all sub-variants in this style
    setApplying(true);
    onAssetsGenerated?.(variations);
    onClose();
  }

  const displayed = showStarred ? variations.filter(a => starredIds.has(a.id)) : variations;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Explore {config.label}</h3>
            <p className="text-xs text-gray-500">Generate variations, pick favorites, refine your style</p>
          </div>
          <div className="flex items-center gap-2">
            {starredIds.size > 0 && (
              <button onClick={() => setShowStarred(prev => !prev)}
                className={`text-xs px-2.5 py-1 rounded-lg border ${showStarred ? 'bg-yellow-50 border-yellow-300 text-yellow-700' : 'border-gray-200 text-gray-400'}`}>
                {showStarred ? `\u2605 ${starredIds.size}` : `\u2606 ${starredIds.size}`}
              </button>
            )}
            <button onClick={handleGenerate} disabled={generating}
              className="text-xs px-3 py-1.5 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40">
              {generating ? 'Generating...' : variations.length > 0 ? 'Generate More' : 'Generate'}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
          </div>
        </div>

        {/* Progress */}
        {generating && (
          <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-200 flex items-center gap-3">
            <div className="w-4 h-4 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
            <span className="text-xs text-indigo-700">{progress}</span>
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {displayed.length === 0 && !generating && (
            <div className="text-center py-12 text-gray-400 text-sm">No variations yet</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayed.map(asset => {
              const isHtml = asset.file_format === 'html' || asset.content?.includes('<');
              const isSelected = selectedIds.includes(asset.id);
              const isStarred = starredIds.has(asset.id);
              return (
                <div key={asset.id} className={`border-2 rounded-xl overflow-hidden transition-all ${isSelected ? 'border-blue-500 ring-1 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}`}>
                  {/* Preview */}
                  <div className="aspect-video bg-white relative overflow-hidden">
                    {isHtml && asset.content ? (
                      <iframe srcDoc={asset.content} className="absolute inset-0 border-0 pointer-events-none origin-top-left" style={{ width: '500%', height: '500%', transform: 'scale(0.2)' }} title={asset.subtype} sandbox="allow-same-origin allow-scripts" />
                    ) : <span className="text-gray-300 text-xs p-4">{asset.subtype?.replace(/_/g, ' ')}</span>}
                    {/* Star */}
                    <button onClick={() => toggleStar(asset.id)}
                      className={`absolute top-2 left-2 text-lg leading-none z-10 drop-shadow ${isStarred ? 'text-yellow-400' : 'text-white/60 hover:text-yellow-300'}`}>
                      {isStarred ? '\u2605' : '\u2606'}
                    </button>
                    {/* Select checkbox */}
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(asset.id)}
                      className="absolute top-2 right-2 w-4 h-4 z-10" />
                  </div>
                  {/* Actions */}
                  <div className="p-2 flex items-center gap-1">
                    <span className="text-[9px] text-gray-500 truncate flex-1">{asset.subtype?.replace(/_/g, ' ')}</span>
                    <button onClick={() => handleUseThis(asset.id)} className="text-[8px] px-2 py-1 bg-gray-900 text-white rounded hover:bg-gray-700">Use This</button>
                    <button onClick={() => handleMoreLike(asset.id)} disabled={!!generatingMore}
                      className="text-[8px] px-2 py-1 border border-gray-200 text-gray-500 rounded hover:bg-gray-50 disabled:opacity-40">
                      {generatingMore === asset.id ? '...' : 'More'}
                    </button>
                    <button onClick={() => handleDelete(asset.id)} className="text-[8px] px-1 py-1 text-red-400 hover:text-red-600">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Refine panel */}
          {selectedIds.length > 0 && (
            <div className="mt-4 border border-blue-200 bg-blue-50 rounded-xl p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Refine {selectedIds.length} selected — what would you change?</label>
              <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={2}
                placeholder="e.g. Use a darker background, add more whitespace, make the CTA button bigger..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none mb-3" />
              <button onClick={handleRefine} disabled={!feedback.trim() || !!generatingMore}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-40 text-sm">
                {generatingMore === 'refining' ? 'Refining...' : `Refine \u2192 New Variations`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
